import {
  AddRounded,
  ArchiveOutlined,
  AttachFile,
  ChatBubbleOutline,
  CheckCircleOutline,
  Close as CloseIcon,
  ErrorOutline,
  FactCheckOutlined,
  InboxOutlined,
  KeyboardArrowDown,
  KeyboardArrowRight,
  LocalOfferOutlined,
  RadioButtonUnchecked,
  Search,
  StarOutlineRounded,
  StarRounded,
  TuneRounded,
} from "@mui/icons-material";
import { Box, IconButton, InputBase, Tooltip } from "@mui/material";
import isNil from "lodash/isNil";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { formatDateTime } from "../../../../_src_shared/utils/functions/formatDateTime";
import { useChecklistRouteHook } from "../../../../hooks/useChecklistRouteHook";
import { loanFetchedDataUwRequirementsSet } from "../../../../redux/reducers/loanFetchedDataReducer";
import LoanDrilldownTabPanel from "../LoanDrilldownTabPanel";

// ── Palette (Tailwind slate / sky / rose / emerald / amber / violet) ──
const SLATE = {
  50: "#f8fafc",
  100: "#f1f5f9",
  200: "#e2e8f0",
  300: "#cbd5e1",
  400: "#94a3b8",
  500: "#64748b",
  600: "#475569",
  700: "#334155",
  800: "#1e293b",
  900: "#0f172a",
};
const SKY = { 50: "#f0f9ff", 500: "#0ea5e9", 600: "#0284c7" };
const ROSE = { 50: "#fff1f2", 500: "#f43f5e", 700: "#be123c" };
const EMERALD = { 50: "#ecfdf5", 700: "#047857" };
const AMBER = { 50: "#fffbeb", 300: "#fcd34d", 500: "#f59e0b", 700: "#b45309" };
const VIOLET = { 50: "#f5f3ff", 500: "#8b5cf6", 600: "#7c3aed" };

const TYPE = {
  CONDITION: "condition",
  EXCEPTION: "exception",
  COMMENT: "comment",
};

const TYPE_CONFIG = {
  [TYPE.CONDITION]: {
    label: "Condition",
    plural: "Conditions",
    Icon: FactCheckOutlined,
    accent: SKY[600],
    bg: SKY[50],
    dot: SKY[500],
  },
  [TYPE.EXCEPTION]: {
    label: "Exception",
    plural: "Exceptions",
    Icon: ErrorOutline,
    accent: ROSE[700],
    bg: ROSE[50],
    dot: ROSE[500],
  },
  [TYPE.COMMENT]: {
    label: "Comment",
    plural: "Comments",
    Icon: ChatBubbleOutline,
    accent: VIOLET[600],
    bg: VIOLET[50],
    dot: VIOLET[500],
  },
};

const STATUS_CONFIG = {
  approved: {
    label: "Approved",
    text: EMERALD[700],
    bg: EMERALD[50],
    ring: "rgba(5,150,105,0.2)",
  },
  accepted: {
    label: "Accepted",
    text: EMERALD[700],
    bg: EMERALD[50],
    ring: "rgba(5,150,105,0.2)",
  },
  pending: {
    label: "Pending",
    text: AMBER[700],
    bg: AMBER[50],
    ring: "rgba(217,119,6,0.2)",
  },
  rejected: {
    label: "Rejected",
    text: ROSE[700],
    bg: ROSE[50],
    ring: "rgba(190,18,60,0.2)",
  },
};

// Maps the many backend status strings down to the four UI buckets.
const normalizeStatus = (status) => {
  if (isNil(status)) return null;
  const s = String(status).toLowerCase();
  if (s === "accepted") return "accepted";
  if (s === "approved" || s === "resolved") return "approved";
  if (s === "rejected") return "rejected";
  return "pending";
};

const stripHtml = (html) =>
  typeof html === "string"
    ? html
        .replace(/<[^>]*>/g, " ")
        .replace(/&nbsp;/g, " ")
        .replace(/\s+/g, " ")
        .trim()
    : "";

const truncate = (text, max = 90) => {
  if (!text) return "";
  return text.length <= max ? text : text.slice(0, max).trim() + "…";
};

// These render condition/comment TIMESTAMPS, so they route through
// `formatDateTime` (UTC→Eastern) — including the same-day / same-year
// comparisons, which compare Eastern calendar fields so the relative label
// matches the displayed time. See the "Dates & times" convention in CLAUDE.md.
const formatTime = (iso) => {
  if (!iso) return "";
  const day = formatDateTime(iso, "yyyy-MM-dd");
  if (!day) return ""; // invalid date
  const nowIso = new Date().toISOString();
  if (day === formatDateTime(nowIso, "yyyy-MM-dd")) {
    return formatDateTime(iso, "h:mm a");
  }
  if (formatDateTime(iso, "yyyy") === formatDateTime(nowIso, "yyyy")) {
    return formatDateTime(iso, "MMM d");
  }
  return formatDateTime(iso, "MMM d, yy");
};

const formatExact = (iso) => {
  if (!iso) return "";
  return formatDateTime(iso, "EEE, MMM d, yyyy h:mm a");
};

const getInitials = (email) => {
  if (!email) return "?";
  const name = String(email).split("@")[0];
  const parts = name.split(/[._-]/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
};

const avatarStyle = (email) => {
  if (!email) return { backgroundColor: SLATE[100], color: SLATE[600] };
  let hash = 0;
  for (let i = 0; i < email.length; i += 1) {
    hash = (hash << 5) - hash + email.charCodeAt(i);
  }
  const hue = Math.abs(hash) % 360;
  return {
    backgroundColor: `hsl(${hue}, 45%, 92%)`,
    color: `hsl(${hue}, 55%, 32%)`,
  };
};

const getName = (email) => (email ? String(email).split("@")[0] : "—");

// ── Item adapters ──────────────────────────────────────────
const adaptCondition = (c) => {
  const stripped = stripHtml(c?.htmlString);
  return {
    id: c?._id,
    type: TYPE.CONDITION,
    status: normalizeStatus(c?.status),
    title: c?.requirementType || truncate(stripped, 90) || "Condition",
    preview: stripped,
    body: c?.htmlString || "",
    author: c?.assignedTo || c?.creators?.[0] || null,
    scope: c?.docType ? { label: "Document", target: c.docType } : null,
    meta: {},
    timestamp: c?.updateDateTime || c?.dateAdded || null,
    raw: c,
  };
};

const adaptException = (e) => {
  const stripped =
    stripHtml(e?.htmlString) || stripHtml(e?.exceptionDescription);
  return {
    id: e?._id,
    type: TYPE.EXCEPTION,
    status: normalizeStatus(e?.exceptionStatus),
    title:
      e?.exceptionCategory ||
      truncate(stripped, 90) ||
      "Exception",
    preview: stripped,
    body: e?.htmlString || e?.exceptionDescription || "",
    author: e?.createdBy || e?.assignedTo || null,
    scope: e?.exceptionCategory
      ? { label: "Exception", target: e.exceptionCategory }
      : null,
    meta: { compensating: e?.compensatingFactors || null },
    timestamp: e?.updateDateTime || e?.dateAdded || null,
    raw: e,
  };
};

const adaptComment = (c) => {
  const stripped = stripHtml(c?.htmlString);
  return {
    id: c?._id,
    type: TYPE.COMMENT,
    status: null,
    title: truncate(stripped, 90) || "Comment",
    preview: stripped,
    body: c?.htmlString || "",
    author: c?.postedBy || null,
    scope: null,
    meta: {},
    timestamp: c?.commentDateTime || c?.createDateTime || null,
    raw: c,
  };
};

// ── Main tab ───────────────────────────────────────────────
const LoanDrilldownConditionsAndExceptionsTab = () => {
  const dispatch = useDispatch();
  const loan = useSelector((s) => s.loanDrilldown);
  const uwRequirements = useSelector(
    (s) => s.loanFetchedData?.uwRequirements,
  );
  const { getLoanUnderwritingRequirements } = useChecklistRouteHook();

  useEffect(() => {
    if (isNil(uwRequirements) && !isNil(loan?._id)) {
      getLoanUnderwritingRequirements({
        dealId: loan._id,
        onSuccessFn: (data) =>
          dispatch(loanFetchedDataUwRequirementsSet(data)),
      });
    }
  }, [uwRequirements, loan?._id]);

  const allItems = useMemo(() => {
    const items = [
      ...(uwRequirements || []).map(adaptCondition),
      ...(loan?.exceptions || []).map(adaptException),
      ...(loan?.comments || []).map(adaptComment),
    ];
    items.sort((a, b) => {
      const ta = a.timestamp ? new Date(a.timestamp).getTime() : 0;
      const tb = b.timestamp ? new Date(b.timestamp).getTime() : 0;
      return tb - ta;
    });
    return items;
  }, [uwRequirements, loan?.exceptions, loan?.comments]);

  const [activeFolder, setActiveFolder] = useState("all");
  const [selectedId, setSelectedId] = useState(null);
  const [search, setSearch] = useState("");
  const [starredIds, setStarredIds] = useState(() => new Set());

  const isStarred = (id) => starredIds.has(id);
  const toggleStar = (id) => {
    setStarredIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const counts = useMemo(() => {
    const c = {
      all: allItems.length,
      [TYPE.CONDITION]: 0,
      [TYPE.EXCEPTION]: 0,
      [TYPE.COMMENT]: 0,
      starred: 0,
      pending: 0,
    };
    allItems.forEach((i) => {
      c[i.type] += 1;
      if (i.status === "pending") c.pending += 1;
      if (starredIds.has(i.id)) c.starred += 1;
    });
    return c;
  }, [allItems, starredIds]);

  const filtered = useMemo(() => {
    return allItems.filter((item) => {
      if (activeFolder === "starred" && !starredIds.has(item.id)) return false;
      if (activeFolder === "pending" && item.status !== "pending") return false;
      if (
        [TYPE.CONDITION, TYPE.EXCEPTION, TYPE.COMMENT].includes(activeFolder) &&
        item.type !== activeFolder
      ) {
        return false;
      }
      if (search) {
        const q = search.toLowerCase();
        const haystack = [
          item.title,
          item.author,
          item.preview,
          item.scope?.target,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [allItems, activeFolder, search, starredIds]);

  // Auto-select the first visible item when the current selection drops out.
  useEffect(() => {
    if (filtered.length === 0) {
      if (selectedId !== null) setSelectedId(null);
      return;
    }
    const stillVisible = filtered.some((i) => i.id === selectedId);
    if (!stillVisible) setSelectedId(filtered[0].id);
  }, [filtered, selectedId]);

  const selected = useMemo(
    () => allItems.find((i) => i.id === selectedId) || null,
    [allItems, selectedId],
  );

  return (
    <LoanDrilldownTabPanel sx={{ flexGrow: 1, minHeight: 0 }}>
      <Box
        sx={{
          display: "flex",
          flexGrow: 1,
          minHeight: 0,
          backgroundColor: SLATE[50],
          color: SLATE[900],
          fontFamily: "var(--ibm)",
          letterSpacing: "-0.005em",
        }}
      >
        <Sidebar
          activeFolder={activeFolder}
          setActiveFolder={setActiveFolder}
          counts={counts}
          loanNumber={loan?.loanNumber}
        />
        <ListColumn
          items={filtered}
          selectedId={selected?.id ?? null}
          setSelectedId={setSelectedId}
          search={search}
          setSearch={setSearch}
          isStarred={isStarred}
          toggleStar={toggleStar}
        />
        <DetailPane
          selected={selected}
          loanNumber={loan?.loanNumber}
          isStarred={selected ? isStarred(selected.id) : false}
          toggleStar={toggleStar}
        />
      </Box>
    </LoanDrilldownTabPanel>
  );
};

// ── Sidebar ────────────────────────────────────────────────
const Sidebar = ({ activeFolder, setActiveFolder, counts, loanNumber }) => {
  const folders = [
    { id: "all", label: "All items", Icon: InboxOutlined, count: counts.all },
    {
      id: "starred",
      label: "Starred",
      Icon: StarOutlineRounded,
      count: counts.starred,
    },
    {
      id: "pending",
      label: "Needs review",
      Icon: RadioButtonUnchecked,
      count: counts.pending,
    },
  ];
  const types = [
    {
      id: TYPE.CONDITION,
      label: "Conditions",
      count: counts[TYPE.CONDITION],
    },
    {
      id: TYPE.EXCEPTION,
      label: "Exceptions",
      count: counts[TYPE.EXCEPTION],
    },
    { id: TYPE.COMMENT, label: "Comments", count: counts[TYPE.COMMENT] },
  ];

  return (
    <Box
      component="aside"
      sx={{
        width: 224,
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        borderRight: `1px solid ${SLATE[200]}`,
        backgroundColor: "#ffffff",
      }}
    >
      <Box
        sx={{
          height: 56,
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 2,
          borderBottom: `1px solid ${SLATE[200]}`,
        }}
      >
        <Box
          sx={{
            height: 28,
            width: 28,
            borderRadius: "6px",
            backgroundColor: SLATE[900],
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <FactCheckOutlined sx={{ fontSize: 15, color: "#ffffff" }} />
        </Box>
        <Box
          sx={{
            fontSize: 14,
            fontWeight: 600,
            letterSpacing: "-0.01em",
          }}
        >
          {loanNumber ? `Loan #${loanNumber}` : "Loan"}
        </Box>
      </Box>

      <Box
        component="button"
        type="button"
        sx={{
          mx: 1.5,
          mt: 1.5,
          py: 1,
          px: 1.5,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.75,
          fontSize: 13,
          fontWeight: 500,
          color: "#ffffff",
          backgroundColor: SLATE[900],
          border: "none",
          borderRadius: "6px",
          cursor: "pointer",
          transition: "background-color 150ms",
          fontFamily: "inherit",
          "&:hover": { backgroundColor: SLATE[800] },
        }}
      >
        <AddRounded sx={{ fontSize: 16 }} />
        New item
      </Box>

      <Box
        component="nav"
        sx={{
          mt: 2,
          flex: 1,
          overflowY: "auto",
          px: 1,
        }}
      >
        <SidebarSectionLabel>Views</SidebarSectionLabel>
        {folders.map((f) => (
          <SidebarRow
            key={f.id}
            active={activeFolder === f.id}
            onClick={() => setActiveFolder(f.id)}
            leading={<f.Icon sx={{ fontSize: 14 }} />}
            label={f.label}
            count={f.count}
          />
        ))}

        <Box sx={{ mt: 2.5 }}>
          <SidebarSectionLabel>Type</SidebarSectionLabel>
        </Box>
        {types.map((t) => {
          const cfg = TYPE_CONFIG[t.id];
          return (
            <SidebarRow
              key={t.id}
              active={activeFolder === t.id}
              onClick={() => setActiveFolder(t.id)}
              leading={
                <Box
                  sx={{
                    height: 8,
                    width: 8,
                    borderRadius: "999px",
                    backgroundColor: cfg.dot,
                  }}
                />
              }
              label={t.label}
              count={t.count}
            />
          );
        })}
      </Box>

      <Box sx={{ borderTop: `1px solid ${SLATE[200]}`, p: 1.5 }}>
        <Box
          sx={{
            borderRadius: "6px",
            backgroundColor: SLATE[50],
            p: 1.25,
          }}
        >
          <Box sx={{ fontSize: 11, fontWeight: 500, color: SLATE[500] }}>
            Loan summary
          </Box>
          <SummaryRow label="Open conditions" value={counts.pending} />
          <SummaryRow label="Total items" value={counts.all} compact />
        </Box>
      </Box>
    </Box>
  );
};

const SidebarSectionLabel = ({ children }) => (
  <Box
    sx={{
      mb: 0.5,
      px: 1,
      fontSize: 10.5,
      fontWeight: 600,
      textTransform: "uppercase",
      letterSpacing: "0.06em",
      color: SLATE[400],
    }}
  >
    {children}
  </Box>
);

const SidebarRow = ({ active, onClick, leading, label, count }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1,
      width: "100%",
      px: 1,
      py: 0.75,
      borderRadius: "6px",
      fontSize: 13,
      backgroundColor: active ? SLATE[100] : "transparent",
      color: active ? SLATE[900] : SLATE[600],
      border: "none",
      cursor: "pointer",
      textAlign: "left",
      fontFamily: "inherit",
      transition: "all 150ms",
      "&:hover": {
        backgroundColor: active ? SLATE[100] : SLATE[50],
        color: SLATE[900],
      },
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        color: active ? SLATE[700] : SLATE[400],
      }}
    >
      {leading}
    </Box>
    <Box sx={{ fontWeight: 500 }}>{label}</Box>
    <Box
      sx={{
        ml: "auto",
        fontSize: 11.5,
        fontVariantNumeric: "tabular-nums",
        color: SLATE[400],
      }}
    >
      {count}
    </Box>
  </Box>
);

const SummaryRow = ({ label, value, compact }) => (
  <Box
    sx={{
      mt: compact ? 0.25 : 0.5,
      display: "flex",
      alignItems: "baseline",
      justifyContent: "space-between",
    }}
  >
    <Box sx={{ fontSize: 12.5, color: SLATE[600] }}>{label}</Box>
    <Box
      sx={{
        fontSize: 13,
        fontWeight: 600,
        color: SLATE[900],
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {value}
    </Box>
  </Box>
);

// ── List column ────────────────────────────────────────────
const ListColumn = ({
  items,
  selectedId,
  setSelectedId,
  search,
  setSearch,
  isStarred,
  toggleStar,
}) => (
  <Box
    component="section"
    sx={{
      width: 420,
      flexShrink: 0,
      display: "flex",
      flexDirection: "column",
      borderRight: `1px solid ${SLATE[200]}`,
      backgroundColor: "#ffffff",
    }}
  >
    <Box
      sx={{
        height: 56,
        display: "flex",
        alignItems: "center",
        gap: 1,
        borderBottom: `1px solid ${SLATE[200]}`,
        px: 2,
      }}
    >
      <Box sx={{ position: "relative", flex: 1 }}>
        <Search
          sx={{
            position: "absolute",
            left: 8,
            top: "50%",
            transform: "translateY(-50%)",
            fontSize: 14,
            color: SLATE[400],
            pointerEvents: "none",
          }}
        />
        <InputBase
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search this loan…"
          sx={{
            height: 32,
            width: "100%",
            borderRadius: "6px",
            border: `1px solid ${SLATE[200]}`,
            backgroundColor: SLATE[50],
            pl: 3.25,
            pr: 3.25,
            fontSize: 12.5,
            color: SLATE[900],
            transition: "all 150ms",
            "& input::placeholder": { color: SLATE[400], opacity: 1 },
            "&.Mui-focused": {
              borderColor: SLATE[300],
              backgroundColor: "#ffffff",
              boxShadow: "0 0 0 2px rgba(15,23,42,0.05)",
            },
          }}
        />
        {search && (
          <IconButton
            onClick={() => setSearch("")}
            size="small"
            sx={{
              position: "absolute",
              right: 4,
              top: "50%",
              transform: "translateY(-50%)",
              p: 0.25,
              color: SLATE[400],
              "&:hover": {
                backgroundColor: SLATE[200],
                color: SLATE[700],
              },
            }}
          >
            <CloseIcon sx={{ fontSize: 12 }} />
          </IconButton>
        )}
      </Box>
      <Tooltip title="Filters">
        <IconButton
          size="small"
          sx={{
            p: 0.75,
            borderRadius: "6px",
            color: SLATE[500],
            "&:hover": { backgroundColor: SLATE[100], color: SLATE[900] },
          }}
        >
          <TuneRounded sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
    </Box>

    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: 2,
        py: 1,
        borderBottom: `1px solid ${SLATE[100]}`,
      }}
    >
      <Box sx={{ fontSize: 11.5, fontWeight: 500, color: SLATE[500] }}>
        {items.length} {items.length === 1 ? "item" : "items"}
      </Box>
      <Box
        component="button"
        type="button"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          fontSize: 11.5,
          fontWeight: 500,
          color: SLATE[500],
          background: "none",
          border: "none",
          cursor: "pointer",
          fontFamily: "inherit",
          p: 0,
          "&:hover": { color: SLATE[900] },
        }}
      >
        Sort: Newest
        <KeyboardArrowDown sx={{ fontSize: 13 }} />
      </Box>
    </Box>

    <Box sx={{ flex: 1, overflowY: "auto" }}>
      {items.length === 0 ? (
        <ListEmptyState />
      ) : (
        items.map((item) => (
          <ListItemRow
            key={item.id}
            item={item}
            selected={selectedId === item.id}
            onClick={() => setSelectedId(item.id)}
            isStarred={isStarred(item.id)}
            toggleStar={toggleStar}
          />
        ))
      )}
    </Box>
  </Box>
);

const ListEmptyState = () => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      px: 3,
      py: 8,
      textAlign: "center",
    }}
  >
    <InboxOutlined sx={{ fontSize: 28, color: SLATE[300] }} />
    <Box sx={{ mt: 1.5, fontSize: 13, fontWeight: 500, color: SLATE[700] }}>
      No items here
    </Box>
    <Box sx={{ mt: 0.25, fontSize: 12, color: SLATE[500] }}>
      Try a different view or search term.
    </Box>
  </Box>
);

const ListItemRow = ({ item, selected, onClick, isStarred, toggleStar }) => {
  const cfg = TYPE_CONFIG[item.type];
  const status = item.status ? STATUS_CONFIG[item.status] : null;

  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        position: "relative",
        display: "flex",
        gap: 1.25,
        width: "100%",
        borderTop: "none",
        borderLeft: "none",
        borderRight: "none",
        borderBottom: `1px solid ${SLATE[100]}`,
        px: 2,
        py: 1.5,
        textAlign: "left",
        backgroundColor: selected ? SLATE[50] : "transparent",
        cursor: "pointer",
        fontFamily: "inherit",
        transition: "background-color 150ms",
        "&:hover": {
          backgroundColor: selected ? SLATE[50] : "rgba(248,250,252,0.6)",
        },
        "&:hover .row-star": { opacity: 1 },
      }}
    >
      {selected && (
        <Box
          sx={{
            position: "absolute",
            left: 0,
            top: 8,
            bottom: 8,
            width: 3,
            borderTopRightRadius: 4,
            borderBottomRightRadius: 4,
            backgroundColor: SLATE[900],
          }}
        />
      )}

      <Box sx={{ pt: 0.75 }}>
        <Box
          title={cfg.label}
          sx={{
            height: 8,
            width: 8,
            borderRadius: "999px",
            backgroundColor: cfg.dot,
          }}
        />
      </Box>

      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Box
            sx={{
              fontSize: 12,
              fontWeight: 500,
              color: SLATE[900],
              overflow: "hidden",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
            }}
          >
            {getName(item.author)}
          </Box>
          <Box
            title={formatExact(item.timestamp)}
            sx={{
              flexShrink: 0,
              fontSize: 11,
              fontVariantNumeric: "tabular-nums",
              color: SLATE[400],
            }}
          >
            {formatTime(item.timestamp)}
          </Box>
        </Box>

        <Box
          sx={{
            mt: 0.25,
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              fontSize: 10,
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              color: cfg.accent,
            }}
          >
            {cfg.label}
          </Box>
          {status && (
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                px: 0.5,
                py: "1px",
                borderRadius: "4px",
                fontSize: 10,
                fontWeight: 500,
                backgroundColor: status.bg,
                color: status.text,
                boxShadow: `inset 0 0 0 1px ${status.ring}`,
              }}
            >
              {status.label}
            </Box>
          )}
          {item.scope?.target && (
            <Box
              sx={{
                fontSize: 11,
                color: SLATE[500],
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
                minWidth: 0,
                flex: 1,
              }}
            >
              · {item.scope.target}
            </Box>
          )}
        </Box>

        <Box
          sx={{
            mt: 0.5,
            fontSize: 13,
            fontWeight: 500,
            color: SLATE[900],
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {item.title}
        </Box>
        {item.preview && item.preview !== item.title && (
          <Box
            sx={{
              mt: 0.25,
              fontSize: 12,
              color: SLATE[500],
              overflow: "hidden",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
            }}
          >
            {item.preview}
          </Box>
        )}
      </Box>

      <Box
        component="span"
        className="row-star"
        role="button"
        tabIndex={0}
        onClick={(e) => {
          e.stopPropagation();
          toggleStar(item.id);
        }}
        sx={{
          flexShrink: 0,
          display: "inline-flex",
          alignItems: "flex-start",
          p: 0.25,
          borderRadius: "4px",
          cursor: "pointer",
          color: isStarred ? AMBER[500] : SLATE[300],
          opacity: isStarred ? 1 : 0,
          transition: "opacity 150ms, color 150ms",
          "&:hover": { color: AMBER[500] },
        }}
      >
        {isStarred ? (
          <StarRounded sx={{ fontSize: 16 }} />
        ) : (
          <StarOutlineRounded sx={{ fontSize: 16 }} />
        )}
      </Box>
    </Box>
  );
};

// ── Detail pane ────────────────────────────────────────────
const DetailPane = ({ selected, loanNumber, isStarred, toggleStar }) => {
  if (!selected) {
    return (
      <Box
        component="section"
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          backgroundColor: "#ffffff",
        }}
      >
        <InboxOutlined sx={{ fontSize: 36, color: SLATE[300] }} />
        <Box
          sx={{
            mt: 1.5,
            fontSize: 14,
            fontWeight: 500,
            color: SLATE[700],
          }}
        >
          Nothing selected
        </Box>
        <Box sx={{ mt: 0.5, fontSize: 13, color: SLATE[500] }}>
          Choose an item from the list to view its details.
        </Box>
      </Box>
    );
  }

  const cfg = TYPE_CONFIG[selected.type];
  const status = selected.status ? STATUS_CONFIG[selected.status] : null;
  const TypeIcon = cfg.Icon;
  const idLabel = selected.id ? String(selected.id).slice(-6) : "—";

  return (
    <Box
      component="section"
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#ffffff",
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          height: 56,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: `1px solid ${SLATE[200]}`,
          px: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            fontSize: 12,
            color: SLATE[500],
            minWidth: 0,
          }}
        >
          {loanNumber && <Box>Loan #{loanNumber}</Box>}
          <KeyboardArrowRight sx={{ fontSize: 14, color: SLATE[300] }} />
          <Box>{cfg.plural}</Box>
          <KeyboardArrowRight sx={{ fontSize: 14, color: SLATE[300] }} />
          <Box sx={{ color: SLATE[700] }}>#{idLabel}</Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <DetailHeaderButton title="Tag" Icon={LocalOfferOutlined} />
          <DetailHeaderButton title="Archive" Icon={ArchiveOutlined} />
          <DetailHeaderButton
            title={isStarred ? "Unstar" : "Star"}
            Icon={isStarred ? StarRounded : StarOutlineRounded}
            onClick={() => toggleStar(selected.id)}
            activeColor={isStarred ? AMBER[500] : null}
          />
        </Box>
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto", px: 4, py: 3 }}>
        <Box sx={{ mx: "auto", maxWidth: 672 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                px: 0.75,
                py: 0.25,
                borderRadius: "6px",
                fontSize: 11,
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                color: cfg.accent,
                backgroundColor: cfg.bg,
              }}
            >
              <TypeIcon sx={{ fontSize: 13 }} />
              {cfg.label}
            </Box>
            {status && (
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  px: 1,
                  py: 0.25,
                  borderRadius: "999px",
                  fontSize: 11,
                  fontWeight: 500,
                  backgroundColor: status.bg,
                  color: status.text,
                  boxShadow: `inset 0 0 0 1px ${status.ring}`,
                }}
              >
                {(selected.status === "approved" ||
                  selected.status === "accepted") && (
                  <CheckCircleOutline sx={{ fontSize: 12 }} />
                )}
                {status.label}
              </Box>
            )}
            {selected.scope && (
              <Box sx={{ fontSize: 12, color: SLATE[500] }}>
                in{" "}
                <Box
                  component="span"
                  sx={{ fontWeight: 500, color: SLATE[700] }}
                >
                  {selected.scope.label}
                </Box>
                {selected.scope.target && (
                  <>
                    {" / "}
                    <Box
                      component="span"
                      sx={{ fontWeight: 500, color: SLATE[700] }}
                    >
                      {selected.scope.target}
                    </Box>
                  </>
                )}
              </Box>
            )}
          </Box>

          <Box
            sx={{
              mt: 1.5,
              fontSize: 22,
              fontWeight: 600,
              lineHeight: 1.2,
              letterSpacing: "-0.015em",
              color: SLATE[900],
            }}
          >
            {selected.title}
          </Box>

          <Box
            sx={{
              mt: 2,
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              borderBottom: `1px solid ${SLATE[100]}`,
              pb: 2.5,
            }}
          >
            <Box
              sx={{
                height: 32,
                width: 32,
                borderRadius: "999px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 11,
                fontWeight: 600,
                ...avatarStyle(selected.author),
              }}
            >
              {getInitials(selected.author)}
            </Box>
            <Box sx={{ lineHeight: 1.2 }}>
              <Box
                sx={{ fontSize: 13, fontWeight: 500, color: SLATE[900] }}
              >
                {getName(selected.author)}
              </Box>
              <Box sx={{ fontSize: 11.5, color: SLATE[500] }}>
                {selected.author ? `${selected.author} · ` : ""}
                {formatExact(selected.timestamp)}
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              mt: 2.5,
              fontSize: 14,
              lineHeight: 1.65,
              color: SLATE[700],
            }}
          >
            {selected.body ? (
              <Box
                sx={{
                  "& p": { m: 0, mb: 1.25, "&:last-child": { mb: 0 } },
                  "& ul, & ol": { my: 1, pl: 3 },
                  "& li": { mb: 0.5 },
                  "& a": { color: SKY[600] },
                  "& code": {
                    fontFamily:
                      "ui-monospace, SFMono-Regular, Menlo, monospace",
                    fontSize: "0.92em",
                    backgroundColor: SLATE[100],
                    px: 0.5,
                    borderRadius: "4px",
                  },
                }}
                dangerouslySetInnerHTML={{ __html: selected.body }}
              />
            ) : (
              <Box sx={{ fontStyle: "italic", color: SLATE[400] }}>
                No description provided.
              </Box>
            )}
          </Box>

          {selected.meta?.compensating && (
            <Box
              sx={{
                mt: 2.5,
                borderRadius: "8px",
                borderLeft: `2px solid ${AMBER[300]}`,
                backgroundColor: "rgba(255,251,235,0.6)",
                px: 2,
                py: 1.5,
              }}
            >
              <Box
                sx={{
                  fontSize: 10.5,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  color: AMBER[700],
                }}
              >
                Compensating factor
              </Box>
              <Box sx={{ mt: 0.25, fontSize: 13.5, color: SLATE[800] }}>
                {selected.meta.compensating}
              </Box>
            </Box>
          )}

          <Box
            sx={{
              mt: 4,
              borderRadius: "8px",
              border: `1px solid ${SLATE[200]}`,
              backgroundColor: "#ffffff",
            }}
          >
            <InputBase
              multiline
              minRows={3}
              placeholder="Add a comment or reply…"
              sx={{
                width: "100%",
                px: 2,
                py: 1.5,
                fontSize: 13.5,
                color: SLATE[900],
                "& textarea::placeholder": {
                  color: SLATE[400],
                  opacity: 1,
                },
                "&:before, &:after": { display: "none" },
              }}
            />
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderTop: `1px solid ${SLATE[100]}`,
                px: 1.5,
                py: 1,
              }}
            >
              <IconButton
                size="small"
                sx={{
                  p: 0.75,
                  borderRadius: "4px",
                  color: SLATE[500],
                  "&:hover": {
                    backgroundColor: SLATE[100],
                    color: SLATE[900],
                  },
                }}
              >
                <AttachFile sx={{ fontSize: 14 }} />
              </IconButton>
              <Box
                component="button"
                type="button"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  borderRadius: "6px",
                  backgroundColor: SLATE[900],
                  px: 1.25,
                  py: 0.5,
                  fontSize: 12,
                  fontWeight: 500,
                  color: "#ffffff",
                  border: "none",
                  cursor: "pointer",
                  fontFamily: "inherit",
                  transition: "background-color 150ms",
                  "&:hover": { backgroundColor: SLATE[800] },
                }}
              >
                Reply
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const DetailHeaderButton = ({ title, Icon, onClick, activeColor }) => (
  <Tooltip title={title}>
    <IconButton
      size="small"
      onClick={onClick}
      sx={{
        p: 0.75,
        borderRadius: "6px",
        color: activeColor || SLATE[500],
        "&:hover": {
          backgroundColor: SLATE[100],
          color: activeColor || SLATE[900],
        },
      }}
    >
      <Icon sx={{ fontSize: 16 }} />
    </IconButton>
  </Tooltip>
);

export default LoanDrilldownConditionsAndExceptionsTab;
